import {
  clearInvalidToken,
  getAuthToken,
  parseAuthToken,
  redirectToSignIn
} from "@pathwright/utils/src/token/utils"
import { useCallback, useEffect } from "react"
import { DEFAULT_EVENTS, useIdleTimer } from "react-idle-timer"
import { useHeartbeatMutation } from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { lastHeartbeatVar, getHeartbeat } from "./heartbeat"
import handleUpdateHeartbeat from "./update"

const HEARTBEAT_INTERVAL = 60 // seconds

// visibilitychange fires when a tab is hidden or shown, and focus fires when
// the browser restores focus on return. Neither means the network is ready.
// Heartbeats after the first one wait for a pointer, key, or scroll event.
const HEARTBEAT_ACTIVITY_EVENTS = DEFAULT_EVENTS.filter(
  (eventName) => eventName !== "visibilitychange" && eventName !== "focus"
)

const useHeartbeat = () => {
  const { me, school } = usePathwrightContext()
  const hasUser = Boolean(me)

  const [mutation] = useHeartbeatMutation({
    onCompleted: (data) => {
      lastHeartbeatVar(getHeartbeat(data.heartbeat))
    },
    update: handleUpdateHeartbeat
  })

  // Only send the heartbeat when the school is activated.
  const sendHeartbeat = useCallback(() => {
    // The shell can still be showing `me` after the JWT was removed and the
    // session cookie expired. REST then fails with "Authentication credentials
    // were not provided." clearInvalidToken does not navigate once the token
    // is already gone, so send the signed-in shell to sign-in.
    if (hasUser && !getAuthToken()) {
      redirectToSignIn()
      return
    }

    if (school.is_activated) {
      try {
        parseAuthToken()
        // A dropped connection rejects this promise. Catch it so the abort is
        // not reported as an unhandled rejection. The next interaction sends
        // again once the interval has passed.
        // void mutation().catch(() => undefined)
        // For now, we'll let the mutation fail so we can continue to track occurrences
        // of the issue in Sentry.
        void mutation()
      } catch {
        clearInvalidToken()
      }
    } else {
      // Update the last heartbeat in state when we can't perform the mutation. Other client behaviors rely on
      // the heartbeat time (such as refresh token).
      lastHeartbeatVar(getHeartbeat())
    }
  }, [hasUser, school.is_activated, mutation])

  const handleHeartbeat = useCallback(() => {
    // Diff of the time of the last reported action and the current time.
    const secondsDiff =
      Math.abs(new Date().getTime() - lastHeartbeatVar().time.getTime()) / 1000
    // If the diff is greater than our heartbeat seconds settings, then report
    // a new action.
    if (secondsDiff > HEARTBEAT_INTERVAL) {
      sendHeartbeat()
    }
  }, [sendHeartbeat])

  const { resume, pause } = useIdleTimer({
    onActive: handleHeartbeat,
    timeout: 1000,
    eventsThrottle: 3000,
    events: HEARTBEAT_ACTIVITY_EVENTS,
    startManually: !me
  })

  useEffect(() => {
    if (me) {
      resume()
      // Always send the heartbeat when authenticated user changes
      // (or hook mounts with authenticated user).
      sendHeartbeat()
    } else {
      pause()
    }
  }, [me, resume, pause, sendHeartbeat])
}

export function Heartbeat() {
  useHeartbeat()
  return null
}

export default useHeartbeat
